import React, {useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import StyledContextWrapper from 'components/templates/Wrappers/Sidebars/ContextWrapper/ContextWrapper.styles';
import Wrapper, {useWrapper} from 'components/templates/Wrappers/Basic/Wrapper/Wrapper';
import ContextDrawer from 'components/organisms/Drawers/ContextDrawer/ContextDrawer';
import utils from 'helpers/utils';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useDialog} from 'components/organisms/Dialogs/Dialog/Dialog';

export const ContextWrapperContext = React.createContext(null)

export function useContextWrapper () {
  return React.useContext(ContextWrapperContext);
}

const ContextWrapper = React.forwardRef((props, ref) => {
  const {
    context,
    anchor,
    variant,
    size,
    open,
    offset,
    animate,
    onToggle,
    ContextDrawerProps,
    ...innerProps
  } = useComponentProps({
    anchor: 'right',
    variant: 'standard',
    animate: true,
    ...props
  }, 'ContextWrapper', {
    static: ['open', 'animate'],
    variable: ['anchor'],
    children: ['content', 'drawer']
  });

  const dialog = useDialog();
  const parentWrapper = useWrapper();

  const innerRef = useRef(null);
  const drawerRef = useRef(null);
  const contextWrapper = useMemo(() => ({
    refs: {
      ref: innerRef,
      drawerRef
    }
  }), []);

  useImperativeHandle(ref, () => contextWrapper);

  const xsUp = useMediaQuery((theme) => theme.breakpoints.up('xs'));
  const smUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const xlUp = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  const vlUp = useMediaQuery((theme) => theme.breakpoints.up('vl'));

  let width = innerProps.theme.layout(280 + (offset ?? 0));
  switch (size) {
    case 'smallest':
      width = innerProps.theme.layout((vlUp ? 196 : (xlUp ? 172 : (lgUp ? 148 : (smUp ? 124 : 124)))) + (offset ?? 0));
      break;
    case 'smaller':
      width = innerProps.theme.layout((vlUp ? 272 : (xlUp ? 248 : (lgUp ? 224 : (smUp ? 184 : 160)))) + (offset ?? 0));
      break;
    case 'small':
      width = innerProps.theme.layout((vlUp ? 320 : (xlUp ? 300 : (lgUp ? 280 : (smUp ? 236 : 212)))) + (offset ?? 0));
      break;
    case 'smallish':
      width = innerProps.theme.layout((vlUp ? 360 : (xlUp ? 340 : (lgUp ? 320 : (smUp ? 280 : 236)))) + (offset ?? 0));
      break;
    case 'medium':
      width = innerProps.theme.layout((vlUp ? 440 : (xlUp ? 400 : (lgUp ? 335 : (smUp ? 300 : 280)))) + (offset ?? 0));
      break;
    case 'large':
      width = innerProps.theme.layout((vlUp ? 550 : (xlUp ? 500 : (lgUp ? 448 : (smUp ? 400 : (xsUp ? 460 : 300))))) + (offset ?? 0));
      break;
    case 'larger':
      width = innerProps.theme.layout((vlUp ? 640 : (xlUp ? 580 : (lgUp ? 520 : (smUp ? 460 : (xsUp ? 420 : 320))))) + (offset ?? 0));
      break;
    case 'largest':
      width = innerProps.theme.layout((vlUp ? 700 : (xlUp ? 660 : (lgUp ? 600 : (smUp ? 500 : (xsUp ? 440 : 340))))) + (offset ?? 0));
      break;
    case 'huge':
      width = innerProps.theme.layout((vlUp ? 720 : (xlUp ? 696 : (lgUp ? 660 : (smUp ? 526 : (xsUp ? 460 : 360))))) + (offset ?? 0));
      break;
    case 'oversize':
      width = innerProps.theme.layout((vlUp ? 744 : (xlUp ? 720 : (lgUp ? 696 : (smUp ? 546 : (xsUp ? 480 : 360))))) + (offset ?? 0));
      break;
    default:
      width = utils.isNumber(size) ? innerProps.theme.layout(size + (offset ?? 0)) : innerProps.theme.layout(280);
  }

  if (!open) {
    width = variant === 'foldout' ? width : '0rem';
  }

  const wrapper = useMemo(() => {
    return {
      bounds: {
        left: variant === 'foldout' ? '0rem' : (anchor === 'left' ? width : '0rem'),
        right: variant === 'foldout' ? '0rem' : (anchor === 'right' ? width : '0rem')
      },
      scrollBounds: {
        left: variant === 'foldout' ? '0rem' : (anchor === 'left' ? width : '0rem'),
        right: variant === 'foldout' ? '0rem' : (anchor === 'right' ? width : '0rem')
      }
    };
  }, [anchor, width, variant]);

  const renderContent = () => {
    return <Wrapper className="ContextWrapper-content"
                    orientation="horizontal"
                    scroll={variant !== 'foldout'}
                    wrapper={wrapper}>
      {innerProps.children}
    </Wrapper>
  }

  const renderDrawer = () => {
    return <ContextDrawer ref={contextWrapper.refs.drawerRef}
                          className="ContextWrapper-drawer"
                          anchor={anchor}
                          width={width}
                          open={Boolean(open)}
                          variant={['foldout', 'temporary'].includes(variant) ? 'temporary' : 'persistent'}
                          onToggle={onToggle}
                          ModalProps={{
                            keepMounted: !['foldout', 'temporary'].includes(variant)
                          }}
                          {...ContextDrawerProps}>
      {context}
    </ContextDrawer>
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <ContextWrapperContext.Provider value={contextWrapper}>
    <StyledContextWrapper ref={contextWrapper.refs.ref} {...innerProps}
                          $dialog={Boolean(dialog)}
                          $wrapper={parentWrapper}>
      {anchor === 'left' ? renderDrawer() : renderContent()}
      {anchor === 'left' ? renderContent() : renderDrawer()}
    </StyledContextWrapper>
  </ContextWrapperContext.Provider>
});

ContextWrapper.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  context: PropTypes.any,
  anchor: PropTypes.string,
  size: PropTypes.any,
  open: PropTypes.bool,
  offset: PropTypes.number,
  animate: PropTypes.bool,
  onToggle: PropTypes.func,
  variant: PropTypes.oneOfType([PropTypes.oneOf(['standard', 'temporary', 'foldout']), PropTypes.string]),
  ContextDrawerProps: PropTypes.object
};

export default ContextWrapper;
